
import * as React from "react"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { ChevronUp, ChevronDown } from "lucide-react"

interface InputNumberProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  value: number
  onChange: (value: number) => void
  min?: number
  max?: number
  step?: number
  className?: string
  compact?: boolean
}

const InputNumber = React.forwardRef<HTMLInputElement, InputNumberProps>(
  ({ className, value, onChange, min = 1, max = 100, step = 1, compact = false, ...props }, ref) => {
    const handleIncrement = () => {
      const newValue = Math.min((value || 0) + step, max)
      onChange(newValue)
    }

    const handleDecrement = () => {
      const newValue = Math.max((value || 0) - step, min)
      onChange(newValue)
    }

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = parseInt(e.target.value, 10)
      if (isNaN(inputValue)) {
        onChange(min)
        return
      }

      // Clamp value between min and max
      const newValue = Math.max(min, Math.min(inputValue, max))
      onChange(newValue)
    }

    // Compact version for inline use in tables and product lists
    if (compact) {
      return (
        <div className="flex h-8 items-center rounded-md border">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8 rounded-r-none"
            onClick={handleDecrement}
            disabled={value <= min}
          >
            <ChevronDown className="h-3 w-3" />
          </Button>
          <div className="flex-1 text-center font-medium min-w-[2rem]">
            {value}
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8 rounded-l-none"
            onClick={handleIncrement}
            disabled={value >= max}
          >
            <ChevronUp className="h-3 w-3" />
          </Button>
        </div>
      )
    }

    // Standard version with input field
    return (
      <div className="flex w-full items-center">
        <Input
          type="number"
          ref={ref}
          value={value}
          onChange={handleInputChange}
          min={min}
          max={max}
          step={step}
          className={cn("rounded-r-none text-center", className)}
          {...props}
        />
        <div className="inline-flex h-10 flex-col">
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="h-5 w-8 rounded-none rounded-tr-md border-l-0 px-2"
            onClick={handleIncrement}
            disabled={value >= max}
          >
            <ChevronUp className="h-3 w-3" />
          </Button>
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="h-5 w-8 rounded-none rounded-br-md border-l-0 border-t-0 px-2"
            onClick={handleDecrement}
            disabled={value <= min}
          >
            <ChevronDown className="h-3 w-3" />
          </Button>
        </div>
      </div>
    )
  }
)

InputNumber.displayName = "InputNumber"

export { InputNumber }
